import { createRoute, redirect } from '@tanstack/react-router';
import { SquadIdSchema } from '@voll-intelligence/domain';

import { queryClient, queryKeys } from '@web/core/query';
import { meRepository, projectsRepository, squadsRepository } from '@web/data/repositories';
import { BoardPage } from '@web/ui/board';
import type { BoardMode } from '@web/ui/board/hooks/use_board';
import { CardDetail, TaskDetail } from '@web/ui/card_detail';

import { orgWorkspaceLayoutRoute } from './overview_routes';

export type BoardSearch = { view?: BoardMode };

/**
 * Guard the squad board on `kanban:projects:read` of the parent
 * project. Mirrors the squad-editor guard but accepts read instead of
 * update so any member of the project can open the board, while only
 * project editors hit the `/squad/$squadId/edit` flow.
 */
async function requireSquadBoardAccess(orgSlug: string, squadIdParam: string): Promise<void> {
  const parsed = SquadIdSchema.safeParse(squadIdParam);
  if (!parsed.success) {
    redirect({ throw: true, to: '/orgs/$orgSlug/overview', params: { orgSlug } });
    return;
  }
  const me = await queryClient.ensureQueryData({
    queryKey: queryKeys.me.all,
    queryFn: () => meRepository.getMe(),
  });
  const org = me.orgs.find((o) => o.slug === orgSlug);
  if (!org) {
    redirect({ throw: true, to: '/' });
    return;
  }
  let detail;
  try {
    detail = await queryClient.ensureQueryData({
      queryKey: queryKeys.squads.byId(org.id, parsed.data),
      queryFn: () => squadsRepository.getSquadById(org.id, parsed.data),
    });
  } catch {
    redirect({ throw: true, to: '/orgs/$orgSlug/overview', params: { orgSlug } });
    return;
  }
  let project;
  try {
    project = await queryClient.ensureQueryData({
      queryKey: queryKeys.projects.detail(org.id, detail.projectId),
      queryFn: () => projectsRepository.getProject(org.id, detail.projectId),
    });
  } catch {
    redirect({ throw: true, to: '/orgs/$orgSlug/overview', params: { orgSlug } });
    return;
  }
  if (!project.permissions.includes('kanban:projects:read')) {
    redirect({ throw: true, to: '/orgs/$orgSlug/overview', params: { orgSlug } });
  }
}

export const squadBoardRoute = createRoute({
  getParentRoute: () => orgWorkspaceLayoutRoute,
  path: 'squad/$squadId',
  component: BoardPage,
  validateSearch: (input: Record<string, unknown>): BoardSearch => ({
    view: input.view === 'tasks' ? 'tasks' : 'conversations',
  }),
  beforeLoad: ({ params }) => requireSquadBoardAccess(params.orgSlug, params.squadId),
});

/**
 * Card detail route. Mounts as a child of the squad board so the board
 * stays visible behind the overlay. `$tab` drives which tab body
 * renders inside the card detail (`tasks`, `conversation`, `notes`,
 * `identity`, `history`). Unknown tab values fall back to `tasks`
 * inside the component. Parent of the task detail route, which renders in
 * its `<Outlet />` as the master-detail left column.
 */
export const squadBoardCardTabRoute = createRoute({
  getParentRoute: () => squadBoardRoute,
  path: 'card/$cardId/$tab',
  component: CardDetail,
});

/**
 * Task detail route. Child of the card detail route so the card content stays
 * visible beside it; opened from a task row in the card Tasks tab. Renders in
 * the card detail's `<Outlet />` (the master-detail left column).
 */
export const squadBoardCardTaskRoute = createRoute({
  getParentRoute: () => squadBoardCardTabRoute,
  path: 'task/$taskId',
  component: TaskDetail,
});
